/**
 * Tier selection for scrub media.
 * Ladder: CSS width/DPR + pointer/hover capability + optional user toggle.
 * deviceMemory / saveData consulted when present; ANY missing signal ⇒ constrained.
 * Width alone is never enough (Fold open must not silently take desktop).
 */

export type Tier = 'desktop' | 'mobile';

export const TIER_SPECS = {
  desktop: {
    // 1080p (was 1440p): scrub seeks decode a full keyframe per tick — 1080p
    // halves that cost and matches typical desktop panels.
    width: 1920,
    height: 1080,
    fps: 30,
    durationSec: 4,
    maxMb: 20,
  },
  mobile: {
    width: 1280,
    height: 720,
    fps: 24,
    durationSec: 3,
    maxMb: 8,
  },
} as const;

/** Breakpoints (CSS-style logical widths, not raw device pixels). */
export const WIDTH_DESKTOP = 1024;

/** deviceMemory below this (when present) forces constrained. */
export const MEMORY_DESKTOP_GB = 4;

export interface TierSignals {
  /** CSS viewport width (layout viewport). */
  width: number;
  /** devicePixelRatio */
  dpr: number;
  /**
   * navigator.deviceMemory in GiB when exposed.
   * `undefined` = missing signal (e.g. iOS) ⇒ constrained.
   */
  deviceMemory?: number;
  /**
   * navigator.connection.saveData when exposed.
   * `undefined` = missing signal ⇒ constrained.
   */
  saveData?: boolean;
  /** matchMedia('(hover: hover)').matches */
  hover: boolean;
  /** matchMedia('(pointer: fine)').matches */
  pointerFine: boolean;
  /** Explicit user override from UI toggle. */
  userOverride?: Tier | null;
}

/**
 * True when any of the network/memory signals is absent or indicates pressure.
 * Missing signal ⇒ constrained (iOS exposes neither deviceMemory nor saveData).
 */
export function isConstrained(signals: TierSignals): boolean {
  if (signals.deviceMemory === undefined) return true;
  if (signals.saveData === undefined) return true;
  if (signals.saveData === true) return true;
  if (signals.deviceMemory < MEMORY_DESKTOP_GB) return true;
  return false;
}

/**
 * Desktop requires large CSS width AND fine pointer AND hover.
 * An open Fold (wide, coarse pointer / no hover) must not take desktop.
 */
export function isDesktopCapable(signals: TierSignals): boolean {
  return (
    signals.width >= WIDTH_DESKTOP &&
    signals.hover === true &&
    signals.pointerFine === true
  );
}

export function selectTier(signals: TierSignals): Tier {
  if (signals.userOverride) {
    return signals.userOverride;
  }

  const constrained = isConstrained(signals);

  if (!constrained && isDesktopCapable(signals)) {
    return 'desktop';
  }

  // Constrained devices never escalate to desktop; everything else → mobile.
  return 'mobile';
}

export interface BrowserTierContext {
  userOverride?: Tier | null;
}

/** Read live browser signals (testable via selectTier pure path). */
export function readBrowserSignals(
  ctx: BrowserTierContext = {},
): TierSignals {
  const nav = typeof navigator !== 'undefined' ? navigator : undefined;
  const win = typeof window !== 'undefined' ? window : undefined;

  const deviceMemory =
    nav && 'deviceMemory' in nav
      ? (nav as Navigator & { deviceMemory?: number }).deviceMemory
      : undefined;

  let saveData: boolean | undefined;
  if (nav && 'connection' in nav) {
    const conn = (nav as Navigator & { connection?: { saveData?: boolean } })
      .connection;
    if (conn && typeof conn.saveData === 'boolean') {
      saveData = conn.saveData;
    } else {
      // connection object present but saveData absent ⇒ missing signal
      saveData = undefined;
    }
  } else {
    saveData = undefined;
  }

  const width = win?.innerWidth ?? 0;
  const dpr = win?.devicePixelRatio ?? 1;
  const hover = win?.matchMedia('(hover: hover)').matches ?? false;
  const pointerFine = win?.matchMedia('(pointer: fine)').matches ?? false;

  return {
    width,
    dpr,
    deviceMemory,
    saveData,
    hover,
    pointerFine,
    userOverride: ctx.userOverride ?? null,
  };
}

export function chooseTier(ctx: BrowserTierContext = {}): Tier {
  return selectTier(readBrowserSignals(ctx));
}

// Bump when media files are replaced in place — same-URL videos are cached
// aggressively by browsers and a swap is otherwise invisible until a hard refresh.
export const MEDIA_VERSION = '21';

export function assetPath(
  scene: string,
  tier: Tier,
  kind: 'video' | 'poster',
): string {
  const ext = kind === 'video' ? 'mp4' : 'webp';
  return `assets/${scene}/${tier}.${ext}?v=${MEDIA_VERSION}`;
}
